import React, {useState, useEffect} from 'react';
import {Helmet} from 'react-helmet';
import DisplayMainHeader from '../components/DisplayMainHeader';
import ScrollToTopOnMount from '../components/ScrollToTopOnMount';
import {Col, Row, Container} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import OpeningTimesOffice from '../Text/OpeningTimesOffice';
import SupportPhoneNumber from '../Text/SupportPhoneNumber';
import { formatMobileNumberUK } from "../helpers/utils";
import { useVerification, VerificationStage } from '../contexts/VerificationContext';

const AccountVerificationPage: React.FC = () => {
    const {
        mobileVerified,
        emailVerified,
        email,
        mobile,
        nextEmailAttemptTime,
        nextSmsAttemptTime,
        verificationStage,
        isLoading,
        error,
        resendVerification,
        fetchVerificationState
    } = useVerification();

    const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
    
    useEffect(() => {
        fetchVerificationState();
    }, [fetchVerificationState]);

    useEffect(() => {
        if (!nextEmailAttemptTime || !nextSmsAttemptTime) {
            setSecondsRemaining(0);
            return;
        }

        const emailTs = Date.parse(nextEmailAttemptTime);
        const smsTs = Date.parse(nextSmsAttemptTime);
        const allowedTs = Math.max(emailTs, smsTs);

        const updateCountdown = () => {
            const now = Date.now();
            const diffSec = Math.ceil((allowedTs - now) / 1000);
            setSecondsRemaining(diffSec > 0 ? diffSec : 0);
        };

        updateCountdown();
        const intervalId = setInterval(updateCountdown, 1000);
        return () => clearInterval(intervalId);
    }, [nextEmailAttemptTime, nextSmsAttemptTime]); // Dependencies are times from context

    // await resend then force immediate status refresh
    const handleResend = async () => {
        if (secondsRemaining > 0) return;
        await resendVerification();
        fetchVerificationState();
    };

    const renderStatus = (verified: boolean | null) => {
        if (verified === true) {
            return (
                <span className="text-success">
                    <span className="icon icon-checkmark4" /> Verified
                </span>
            );
        }
        return <img src="images/icons/loading.svg" width={20} height={20} alt="Loading"/>;
    };


    if (verificationStage === VerificationStage.LOADING && isLoading) {
        return (
            <>
                <ScrollToTopOnMount />
                <DisplayMainHeader />
                <Helmet>
                    <title>Account Verification</title>
                </Helmet>
                <Container fluid className="bg-light">
                    <Container>
                        <Row>
                            <Col xs={12} className="pt-50 pb-50 w-800px mx-auto text-center">
                                <img src="/images/icons/loading.svg" width={30} height={30} alt="Loading..." />
                                <p>Loading verification status...</p>
                            </Col>
                        </Row>
                    </Container>
                </Container>
            </>
        );
    }

    if (verificationStage === VerificationStage.ERROR) {
         return (
             <>
                 <ScrollToTopOnMount />
                 <DisplayMainHeader />
                 <Helmet>
                     <title>Verification Error</title>
                 </Helmet>
                 <Container fluid className="bg-light">
                     <Container>
                         <Row>
                             <Col xs={12} className="pt-50 pb-50 w-800px mx-auto text-center box-shadow-v3 bg-light mb-50 mt-50 p-30 brad-30">
                                 <h4 className="text-danger">Error</h4>
                                 <p>Something has gone wrong. Please refresh the page or contact customer support if problem persists</p>
                                 <a className="underline text-dark"
                                    href={`tel:${SupportPhoneNumber.getPhoneNumber()}`}>
                                     <SupportPhoneNumber/>
                                 </a>
                                 <p className="text-muted">{error}</p>
                                 <p className="mt-20">
                                     <Link to="/customer/dashboard" className="btn btn-cta">Go to Dashboard</Link>
                                 </p>
                             </Col>
                         </Row>
                     </Container>
                 </Container>
             </>
         );
     }


    return (
        <>
            <ScrollToTopOnMount />
            <DisplayMainHeader/>
            <Helmet>
                <title>Account Verification</title>
                <meta name="description" content="SwitchboardFREE account verification"/>
                <style>{`
      body.logged-in {
        background: #fff;
      }
    `}</style>
            </Helmet>
            <Container fluid className="bg-light">
                <Container>
                    <Row>
                        <Col xs={12}
                             className="pt-50 pb-50 w-800px mx-auto box-shadow-v3 bg-light mb-50 mt-50 p-30 brad-30">
                            <Row>
                                <Col>
                                    {verificationStage === VerificationStage.WAITING_USER_VERIFICATION && (
                                        <>
                                            <h4 className="text-500 text-center">Account Verification</h4>
                                            <p className="mt-20">
                                                A verification email has been sent to <b>{email}</b> and an SMS
                                                    to{' '}
                                                    <b>{formatMobileNumberUK(mobile)}</b>. Both should arrive
                                                    immediately. If you don’t receive them, please check your
                                                    spam or junk folder. Once you click the verification link in the
                                                    email and SMS, your account will
                                                    be fully verified and you can make and receive calls.
                                                </p>
                                            <div className="verification-steps">
                                                <h6 className="text-500 mt-30">Real-time verification check</h6>
                                                <p className="mb-0">
                                                    <span className="w-50 d-inline-block">Checking if you verified your mobile number</span>
                                                    <span className="text-end float-end w-50">
                                                        <span className="text-600">{renderStatus(mobileVerified)}</span>
                                                    </span>
                                                </p>
                                                <p>
                                                    <span className="w-50 d-inline-block">Checking if you verified your email address</span>
                                                    <span className="text-end float-end w-50">
                                                        <span className="text-600">{renderStatus(emailVerified)}</span>
                                                    </span>
                                                </p>
                                            </div>
                                            <div className="text-center mt-30">
                                                <button
                                                    type="button"
                                                    className="btn btn-cta"
                                                    onClick={handleResend}
                                                    disabled={secondsRemaining > 0 || isLoading} // Disable while loading too
                                                >
                                                    {isLoading ? 'Sending...' : 'Resend'}
                                                </button>
                                                {secondsRemaining > 0 && (
                                                    <p className="d-block mt-10 text-xs">
                                                        You can resend in {secondsRemaining} second{secondsRemaining !== 1 ? 's' : ''}...
                                                    </p>
                                                )}
                                                <p>
                                                    <Link to="/customer/dashboard" className="text-xs underline text-dark cursor-pointer text-400">
                                                        Cancel... I'll do it later
                                                    </Link>
                                                </p>
                                            </div>
                                        </>
                                    )}

                                    {verificationStage === VerificationStage.ALL_COMPLETE && (
                                        <>
                                            <h1 className="text-center h3 text-500 pb-30">Verification Complete</h1>
                                            <div>
                                                <p>You've successfully verified your email and mobile number.</p>
                                                <p>Your account setup is complete!</p>
                                                <p>
                                                    If you have any questions feel free to contact support on{' '}
                                                    <a className="underline text-dark"
                                                       href={`tel:${SupportPhoneNumber.getPhoneNumber()}`}>
                                                        <SupportPhoneNumber/>
                                                    </a>
                                                    . We are open <OpeningTimesOffice/>
                                                </p>
                                            </div>
                                            <p className="text-center">
                                                <Link to="/customer/dashboard" className="btn btn-cta mt-20">
                                                    Go to Dashboard
                                                </Link>
                                            </p>
                                        </>
                                    )}

                                    {verificationStage === VerificationStage.CHECKS_PENDING && (
                                        <>
                                            <h1 className="text-center h3 text-500 pb-30">Thank You for Verifying</h1>
                                            <div>
                                                <p>You've successfully verified your email and mobile number.</p>
                                                <p>Your account setup is complete!</p>
                                                <p>
                                                    We're now completing a few final automated checks in the background
                                                    to finalise your account setup. This usually takes just a few
                                                    minutes.
                                                </p>
                                                <p>There's nothing else you need to do right now — if we need any
                                                    further information, we'll be in touch.</p>
                                                <p>
                                                    If you have any questions feel free to contact support on{' '}
                                                    <a className="underline text-dark"
                                                       href={`tel:${SupportPhoneNumber.getPhoneNumber()}`}>
                                                        <SupportPhoneNumber/>
                                                    </a>
                                                    . We are open <OpeningTimesOffice/>
                                                </p>
                                            </div>
                                            <p className="text-center">
                                                <Link to="/customer/dashboard" className="btn btn-cta mt-20">
                                                    Go to Dashboard (Checks Pending)
                                                </Link>
                                            </p>
                                        </>
                                    )}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </Container>
        </>
    );
};

export default AccountVerificationPage;